import React, { useState, useRef } from 'react';
import { cities } from '../../data/citiesData';
import { 
  Box, 
  Heading, 
  Input, 
  Select, 
  HStack,
  VStack,
  Icon,
  Text,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Flex,
  InputGroup,
  InputLeftElement,
  Stack
} from '@chakra-ui/react';
import { 
  FiFilter, 
  FiSearch, 
  FiMapPin, 
  FiNavigation,
  FiX,
  FiSliders,
  FiCheck 
} from 'react-icons/fi';
import { MdOutlineDirectionsCar } from 'react-icons/md';

const TopFilterBar = ({ 
  carModel, 
  city, 
  region, 
  setCarModel, 
  setCity, 
  setRegion
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [localCarModel, setLocalCarModel] = useState(carModel);
  const [localCity, setLocalCity] = useState(city);
  const [localRegion, setLocalRegion] = useState(region);
  
  // Blue theme colors matching HeroSection
  const accentColor = '#3B82F6'; // Blue-500
  const accentGradient = 'linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)';
  const lightBlue = '#EFF6FF';
  const darkBlue = '#1E40AF';
  
  const bgColor = 'white';
  const textColor = '#1F2937'; // Gray-800
  const borderColor = '#D1D5DB'; // Gray-300
  const inputBg = '#F9FAFB'; // Gray-50
  const hoverBg = '#F3F4F6'; // Gray-100

  // Handle mobile filter submit
  const handleMobileSubmit = () => {
    setCarModel(localCarModel);
    setCity(localCity);
    setRegion(localRegion);
    onClose();
  };

  // Handle mobile filter reset
  const handleMobileReset = () => {
    setLocalCarModel('');
    setLocalCity('');
    setLocalRegion('');
    setCarModel('');
    setCity('');
    setRegion('');
    onClose();
  };

  // Handle desktop filter change
  const handleDesktopCarModelChange = (e) => {
    const value = e.target.value;
    setCarModel(value);
  };

  const handleDesktopCityChange = (e) => {
    const value = e.target.value;
    setCity(value);
  };

  const handleDesktopRegionChange = (e) => {
    const value = e.target.value;
    setRegion(value);
  };

  const handleReset = () => {
    setCarModel('');
    setCity('');
    setRegion('');
  };

  const hasActiveFilters = carModel || city || region;

  // Mobile Filter Drawer
  const MobileFilterDrawer = () => (
    <Drawer 
      isOpen={isOpen} 
      placement="right" 
      onClose={onClose} 
      size="sm"
    >
      <DrawerOverlay bg="blackAlpha.300" backdropFilter="blur(2px)" />
      <DrawerContent 
        bg={bgColor}
        borderLeftRadius="2xl"
        boxShadow="xl"
      >
        <DrawerCloseButton 
          top={4} 
          right={4}
          color={textColor}
          _hover={{ bg: hoverBg }}
        />
        <DrawerHeader 
          pt={8}
          pb={4}
          borderBottom="1px"
          borderColor="blue.100"
          bg={lightBlue}
        >
          <Flex align="center" gap={3}>
            <Box
              p={2}
              borderRadius="lg"
              bgGradient={accentGradient}
            >
              <Icon as={FiSliders} boxSize={5} color="white" />
            </Box>
            <Box>
              <Heading size="md" color={textColor}>
                Refine Search
              </Heading>
              <Text fontSize="sm" color="blue.600" mt={1}>
                Customize your filters
              </Text>
            </Box>
          </Flex>
        </DrawerHeader>
        
        <DrawerBody py={6} px={6}>
          <VStack spacing={6} align="stretch">
            {/* Car Model Filter */}
            <Box>
              <Flex align="center" gap={2} mb={3}>
                <Icon as={MdOutlineDirectionsCar} color={accentColor} />
                <Text fontSize="sm" fontWeight="600" color={textColor}>
                  CAR MODEL
                </Text>
              </Flex>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FiSearch className="text-blue-400" />
                </InputLeftElement>
                <Input
                  placeholder="e.g. Toyota Camry, BMW X5"
                  value={localCarModel}
                  onChange={(e) => setLocalCarModel(e.target.value)}
                  bg={inputBg}
                  borderColor="blue.200"
                  borderRadius="lg"
                  color={textColor}
                  _placeholder={{ color: 'gray.500' }}
                  _hover={{ borderColor: accentColor }}
                  _focus={{
                    borderColor: accentColor,
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                  }}
                  size="md"
                />
              </InputGroup>
            </Box>

            {/* City Filter */}
            <Box>
              <Flex align="center" gap={2} mb={3}>
                <Icon as={FiMapPin} color={accentColor} />
                <Text fontSize="sm" fontWeight="600" color={textColor}>
                  CITY
                </Text>
              </Flex>
              <Select
                placeholder="All Cities in Pakistan"
                value={localCity}
                onChange={(e) => setLocalCity(e.target.value)}
                bg={inputBg}
                borderColor="blue.200"
                borderRadius="lg"
                color={textColor}
                _hover={{ borderColor: accentColor }}
                _focus={{
                  borderColor: accentColor,
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                }}
                size="md"
              >
                {cities.map((cityOption) => (
                  <option 
                    key={cityOption.id} 
                    value={cityOption.city}
                    style={{ 
                      backgroundColor: bgColor,
                      color: textColor
                    }}
                  >
                    {cityOption.city}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Region Filter */}
            <Box>
              <Flex align="center" gap={2} mb={3}>
                <Icon as={FiNavigation} color={accentColor} />
                <Text fontSize="sm" fontWeight="600" color={textColor}>
                  REGION
                </Text>
              </Flex>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FiNavigation className="text-blue-400" />
                </InputLeftElement>
                <Input
                  placeholder="e.g. Downtown, Business Bay"
                  value={localRegion}
                  onChange={(e) => setLocalRegion(e.target.value)}
                  bg={inputBg}
                  borderColor="blue.200"
                  borderRadius="lg"
                  color={textColor}
                  _placeholder={{ color: 'gray.500' }}
                  _hover={{ borderColor: accentColor }}
                  _focus={{
                    borderColor: accentColor,
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                  }}
                  size="md"
                />
              </InputGroup>
            </Box>

            {/* Active Filters Display */}
            {(localCarModel || localCity || localRegion) && (
              <Box p={4} bg={lightBlue} borderRadius="lg">
                <Text fontSize="sm" fontWeight="600" color="blue.700" mb={2}>
                  Active Filters:
                </Text>
                <VStack align="stretch" spacing={1}>
                  {localCarModel && (
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="blue.600">Car Model</Text>
                      <Flex align="center" gap={1}>
                        <Text fontSize="sm" color="blue.800" fontWeight="500">{localCarModel}</Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setLocalCarModel('')}
                          color="blue.500"
                        >
                          <FiX />
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                  {localCity && (
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="blue.600">City</Text>
                      <Flex align="center" gap={1}>
                        <Text fontSize="sm" color="blue.800" fontWeight="500">{localCity}</Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setLocalCity('')}
                          color="blue.500"
                        >
                          <FiX />
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                  {localRegion && (
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="blue.600">Region</Text>
                      <Flex align="center" gap={1}>
                        <Text fontSize="sm" color="blue.800" fontWeight="500">{localRegion}</Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setLocalRegion('')}
                          color="blue.500"
                        >
                          <FiX />
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                </VStack>
              </Box>
            )}

            {/* Action Buttons */}
            <VStack spacing={3} pt={4}>
              <Button
                w="full"
                bgGradient={accentGradient}
                color="white"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg',
                  bgGradient: 'linear-gradient(135deg, #2563EB 0%, #0284C7 100%)'
                }}
                _active={{
                  transform: 'translateY(0)',
                }}
                leftIcon={<FiCheck />}
                onClick={handleMobileSubmit}
                size="lg"
                borderRadius="xl"
              >
                Apply Filters
              </Button>
              <Button
                w="full"
                variant="outline"
                color="blue.600"
                borderColor="blue.300"
                _hover={{ 
                  bg: 'blue.50',
                  borderColor: 'blue.400'
                }}
                onClick={handleMobileReset}
                size="lg"
                borderRadius="xl"
              >
                Clear All
              </Button>
            </VStack>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  return (
    <Box
      w="full"
      bg={bgColor}
      borderBottom="1px"
      borderColor="blue.100"
      boxShadow="0 4px 20px rgba(59, 130, 246, 0.08)"
      px={{ base: 4, sm: 6, lg: 8 }}
      py={{ base: 4, md: 5 }}
    >
      <Stack 
        direction={{ base: 'column', lg: 'row' }} 
        spacing={{ base: 4, lg: 6 }}
        align={{ base: 'stretch', lg: 'center' }}
        justify="space-between"
      >
        {/* Desktop Filter Bar */}
        <Flex 
          flex={1}
          gap={{ base: 4, lg: 6 }}
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'center' }}
        >
          {/* Filter Header */}
          <Flex align="center" gap={3} minW={{ base: 'full', md: '200px' }}>
            <Box
              p={2}
              borderRadius="lg"
              bgGradient={accentGradient}
              display={{ base: 'none', md: 'flex' }}
            >
              <Icon as={FiFilter} boxSize={5} color="white" />
            </Box>
            <Box>
              <Heading size="md" color={textColor} display={{ base: 'none', md: 'block' }}>
                Find Your Car
              </Heading>
              <Text fontSize="sm" color="blue.600" display={{ base: 'none', md: 'block' }}>
                Refine your search
              </Text>
              <Heading size="md" color={textColor} display={{ base: 'block', md: 'none' }}>
                Filters
              </Heading>
            </Box>
          </Flex>

          {/* Filter Inputs */}
          <Flex 
            flex={1}
            gap={4}
            direction={{ base: 'column', md: 'row' }}
          >
            {/* Car Model Input */}
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="600" color={textColor} mb={2} display={{ base: 'none', md: 'block' }}>
                CAR MODEL
              </Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FiSearch className="text-blue-400" />
                </InputLeftElement>
                <Input
                  placeholder="Search car model..."
                  value={carModel}
                  onChange={handleDesktopCarModelChange}
                  bg={inputBg}
                  borderColor="blue.200"
                  borderRadius="lg"
                  color={textColor}
                  _placeholder={{ color: 'gray.500' }}
                  _hover={{ borderColor: accentColor }}
                  _focus={{
                    borderColor: accentColor,
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                  }}
                  size="md"
                />
              </InputGroup>
            </Box>

            {/* City Select */}
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="600" color={textColor} mb={2} display={{ base: 'none', md: 'block' }}>
                CITY
              </Text>
              <Select
                placeholder="Select city"
                value={city}
                onChange={handleDesktopCityChange}
                bg={inputBg}
                borderColor="blue.200"
                borderRadius="lg"
                color={textColor}
                _hover={{ borderColor: accentColor }}
                _focus={{
                  borderColor: accentColor,
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                }}
                size="md"
              >
                {cities.map((cityOption) => (
                  <option 
                    key={cityOption.id} 
                    value={cityOption.city}
                    style={{ 
                      backgroundColor: bgColor,
                      color: textColor
                    }}
                  >
                    {cityOption.city}
                  </option>
                ))}
              </Select>
            </Box>

            {/* Region Input - Hidden on mobile */}
            <Box flex={1} display={{ base: 'none', lg: 'block' }}>
              <Text fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                REGION
              </Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FiNavigation className="text-blue-400" />
                </InputLeftElement>
                <Input
                  placeholder="Enter region..."
                  value={region}
                  onChange={handleDesktopRegionChange}
                  bg={inputBg}
                  borderColor="blue.200"
                  borderRadius="lg"
                  color={textColor}
                  _placeholder={{ color: 'gray.500' }}
                  _hover={{ borderColor: accentColor }}
                  _focus={{
                    borderColor: accentColor,
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                  }}
                  size="md"
                />
              </InputGroup>
            </Box>
          </Flex>

          {/* Action Buttons */}
          <Flex 
            gap={3} 
            align="center"
            mt={{ base: 4, md: 0 }}
          >
            {hasActiveFilters && (
              <Button
                variant="ghost"
                color="blue.600"
                onClick={handleReset}
                leftIcon={<FiX />}
                size="md"
                display={{ base: 'none', md: 'flex' }}
                _hover={{ bg: 'blue.50' }}
              >
                Clear
              </Button>
            )}
            
            {/* Mobile Filter Button */}
            <Button
              leftIcon={<FiSliders />}
              variant="outline"
              color="blue.600"
              borderColor="blue.300"
              _hover={{ 
                bg: 'blue.50',
                borderColor: 'blue.400'
              }}
              onClick={onOpen}
              size="md"
              display={{ base: 'flex', lg: 'none' }}
              width={{ base: 'full', sm: 'auto' }}
            >
              {hasActiveFilters && (
                <Box
                  as="span"
                  w={2}
                  h={2}
                  bg="red.500"
                  borderRadius="full"
                  position="absolute"
                  top={2}
                  right={2}
                />
              )}
              More Filters
            </Button>
          </Flex>
        </Flex>

        {/* Active Filters Display - Desktop */}
        {hasActiveFilters && (
          <Flex 
            align="center" 
            gap={3} 
            p={4} 
            bg={lightBlue} 
            borderRadius="xl"
            display={{ base: 'none', lg: 'flex' }}
          >
            <Text fontSize="sm" fontWeight="600" color="blue.700">
              Active Filters:
            </Text>
            <HStack spacing={2}>
              {carModel && (
                <Flex 
                  align="center" 
                  gap={1}
                  bg="white"
                  px={3}
                  py={1}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontSize="sm" color="blue.800">{carModel}</Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => setCarModel('')}
                    color="blue.500"
                    _hover={{ bg: 'blue.50' }}
                  >
                    <FiX />
                  </Button>
                </Flex>
              )}
              {city && (
                <Flex 
                  align="center" 
                  gap={1}
                  bg="white"
                  px={3}
                  py={1}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontSize="sm" color="blue.800">{city}</Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => setCity('')}
                    color="blue.500"
                    _hover={{ bg: 'blue.50' }}
                  >
                    <FiX />
                  </Button>
                </Flex>
              )}
              {region && (
                <Flex 
                  align="center" 
                  gap={1}
                  bg="white"
                  px={3}
                  py={1}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontSize="sm" color="blue.800">{region}</Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => setRegion('')}
                    color="blue.500"
                    _hover={{ bg: 'blue.50' }}
                  >
                    <FiX />
                  </Button>
                </Flex>
              )}
              <Button
                size="sm"
                variant="ghost"
                color="blue.600"
                onClick={handleReset}
                _hover={{ bg: 'blue.50' }}
              >
                Clear All
              </Button>
            </HStack>
          </Flex>
        )}
      </Stack>

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer />
    </Box>
  );
};

export default TopFilterBar;